'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useLanguage } from '@/components/language-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Link from 'next/link';

interface Article {
  id: number;
  title: string;
  summary: string | null;
  content: string;
  author: string;
  pdf_url: string | null;
  view_count: number;
  created_at: string;
}

interface Comment {
  id: number;
  author_name: string;
  content: string;
  parent_id: number | null;
  created_at: string;
}

const translations = {
  zh: {
    loading: '加载中...',
    notFound: '文章不存在',
    backToList: '返回列表',
    download: '下载PDF',
    author: '作者',
    publishDate: '发布日期',
    views: '次浏览',
    comments: '评论互动',
    noComments: '暂无评论，来抢沙发吧！',
    name: '您的姓名',
    email: '邮箱（选填）',
    comment: '发表评论',
    submit: '提交评论',
    submitting: '提交中...',
    reply: '回复',
    cancel: '取消',
    commentPlaceholder: '分享您的想法或问题...',
    relatedArticles: '相关文章',
  },
  en: {
    loading: 'Loading...',
    notFound: 'Article not found',
    backToList: 'Back to List',
    download: 'Download PDF',
    author: 'Author',
    publishDate: 'Published',
    views: 'views',
    comments: 'Comments & Discussion',
    noComments: 'No comments yet. Be the first to comment!',
    name: 'Your Name',
    email: 'Email (optional)',
    comment: 'Leave a Comment',
    submit: 'Submit',
    submitting: 'Submitting...',
    reply: 'Reply',
    cancel: 'Cancel',
    commentPlaceholder: 'Share your thoughts or questions...',
    relatedArticles: 'Related Articles',
  },
  ja: {
    loading: '読み込み中...',
    notFound: '記事が見つかりません',
    backToList: 'リストに戻る',
    download: 'PDFをダウンロード',
    author: '著者',
    publishDate: '公開日',
    views: '回閲覧',
    comments: 'コメント・ディスカッション',
    noComments: 'コメントはまだありません。最初のコメントを投稿しましょう！',
    name: 'お名前',
    email: 'メール（任意）',
    comment: 'コメントを投稿',
    submit: '送信',
    submitting: '送信中...',
    reply: '返信',
    cancel: 'キャンセル',
    commentPlaceholder: 'ご意見やご質問をお聞かせください...',
    relatedArticles: '関連記事',
  },
};

export default function BlogPostPage() {
  const params = useParams();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.zh;

  const [article, setArticle] = useState<Article | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Comment form state
  const [commentName, setCommentName] = useState('');
  const [commentEmail, setCommentEmail] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (params.slug) {
      fetchArticle();
      fetchComments();
    }
  }, [params.slug, language]);

  const fetchArticle = async () => {
    try {
      const response = await fetch(`/api/articles/${params.slug}?lang=${language}`);
      const data = await response.json();
      setArticle(data.article);
    } catch (error) {
      console.error('Failed to fetch article:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`/api/comments?article_id=${article?.id}&lang=${language}`);
      const data = await response.json();
      setComments(data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleSubmitComment = async (parentId: number | null = null) => {
    if (!commentName.trim() || !commentContent.trim() || !article) return;

    setSubmitting(true);
    try {
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          article_id: article.id,
          parent_id: parentId,
          author_name: commentName,
          author_email: commentEmail,
          content: commentContent,
        }),
      });

      if (response.ok) {
        setCommentContent('');
        setReplyingTo(null);
        fetchComments();
      }
    } catch (error) {
      console.error('Failed to submit comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : language === 'ja' ? 'ja-JP' : 'zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderContent = (content: string) => {
    // Simple markdown-like rendering
    return content.split('\n\n').map((paragraph, index) => {
      if (paragraph.startsWith('# ') && !paragraph.startsWith('##')) {
        return <h1 key={index} className="text-3xl font-bold mt-8 mb-4">{paragraph.replace(/^#\s*/, '')}</h1>;
      }
      if (paragraph.startsWith('###')) {
        return <h3 key={index} className="text-xl font-semibold mt-4 mb-2">{paragraph.replace(/^###\s*/, '')}</h3>;
      }
      if (paragraph.startsWith('##')) {
        return <h2 key={index} className="text-2xl font-bold mt-6 mb-4">{paragraph.replace(/^##\s*/, '')}</h2>;
      }
      if (paragraph.includes('\n|')) {
        // Markdown table rendering (supports caption text above the table within the same block)
        const lines = paragraph.split('\n');
        const tableLines: string[] = [];
        const textLines: string[] = [];
        let inTable = false;
        for (const line of lines) {
          if (line.trim().startsWith('|')) {
            inTable = true;
            tableLines.push(line.trim());
          } else if (line.trim() === '') {
            continue;
          } else {
            textLines.push(line.trim().replace(/\*\*/g, ''));
          }
        }
        const parseRow = (line: string) => line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
        const header = tableLines.length ? parseRow(tableLines[0]) : [];
        const bodyRows = tableLines.slice(1).filter(line => !/^\|[\s:|-]+\|?$/.test(line.replace(/\s/g, ''))).map(parseRow);
        return (
          <div key={index}>
            {textLines.length > 0 && (
              <p className="my-4 font-semibold text-foreground">{textLines.join(' ')}</p>
            )}
            {header.length > 0 && bodyRows.length > 0 && (
              <div className="my-6 overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-primary/5">
                      {header.map((cell, i) => <th key={i} className="border border-border px-3 py-2 text-left font-semibold">{cell}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {bodyRows.map((row, r) => (
                      <tr key={r} className={r % 2 === 1 ? 'bg-muted/30' : ''}>
                        {row.map((cell, c) => <td key={c} className="border border-border px-3 py-2 text-muted-foreground">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      }
      if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) {
        const items = paragraph.split('\n').map(item => item.replace(/^[-*]\s*/, ''));
        return (
          <ul key={index} className="list-disc list-inside my-4 space-y-1">
            {items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
      }
      return <p key={index} className="my-4 text-muted-foreground leading-relaxed">{paragraph}</p>;
    });
  };

  // Group comments by parent
  const getRootComments = () => comments.filter(c => !c.parent_id);
  const getReplies = (parentId: number) => comments.filter(c => c.parent_id === parentId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-muted-foreground">{t.notFound}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Article Header */}
      <header className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <Link href="/blog" className="inline-flex items-center text-sm opacity-80 hover:opacity-100 mb-4">
            ← {t.backToList}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{article.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm opacity-90">
            <span>{t.author}: {article.author}</span>
            <span>{t.publishDate}: {formatDate(article.created_at)}</span>
            <span>{article.view_count} {t.views}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Article Content */}
          <article className="lg:col-span-2">
            {article.summary && (
              <div className="bg-muted/50 border-l-4 border-primary p-4 mb-8 rounded-r">
                <p className="italic text-muted-foreground">{article.summary}</p>
              </div>
            )}
            
            <div className="prose prose-lg max-w-none">
              {renderContent(article.content)}
            </div>

            {article.pdf_url && (
              <div className="mt-8 p-6 bg-muted rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">PDF 版本</h3>
                    <p className="text-sm text-muted-foreground">下载完整的技术文档</p>
                  </div>
                  <Link href={`/api/articles/${article.id}/download`} target="_blank" rel="noopener noreferrer">
                    <Button className="gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t.download}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            {/* Comment Form */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t.comments}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">{t.name} *</Label>
                  <Input
                    id="name"
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    placeholder={t.name}
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={commentEmail}
                    onChange={(e) => setCommentEmail(e.target.value)}
                    placeholder={t.email}
                  />
                </div>
                <div>
                  <Label htmlFor="content">{t.comment} *</Label>
                  <Textarea
                    id="content"
                    value={commentContent}
                    onChange={(e) => setCommentContent(e.target.value)}
                    placeholder={t.commentPlaceholder}
                    rows={4}
                  />
                </div>
                <Button
                  onClick={() => handleSubmitComment(null)}
                  disabled={submitting || !commentName.trim() || !commentContent.trim()}
                  className="w-full"
                >
                  {submitting ? t.submitting : t.submit}
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>

        {/* Comments Section */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-6">{t.comments}</h2>
          
          {comments.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t.noComments}</p>
          ) : (
            <div className="space-y-6">
              {getRootComments().map((comment) => (
                <Card key={comment.id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold">{comment.author_name}</span>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{comment.content}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    >
                      {t.reply}
                    </Button>
                    
                    {/* Reply Form */}
                    {replyingTo === comment.id && (
                      <div className="mt-4 pl-4 border-l-2 border-muted">
                        <Textarea
                          value={commentContent}
                          onChange={(e) => setCommentContent(e.target.value)}
                          placeholder={t.commentPlaceholder}
                          rows={3}
                          className="mb-2"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSubmitComment(comment.id)}
                            disabled={submitting}
                          >
                            {t.submit}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReplyingTo(null)}
                          >
                            {t.cancel}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Replies */}
                    {getReplies(comment.id).map((reply) => (
                      <div key={reply.id} className="mt-4 pl-4 border-l-2 border-muted">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold">{reply.author_name}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(reply.created_at)}
                          </span>
                        </div>
                        <p className="text-muted-foreground">{reply.content}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
